import { useCallback, useEffect, useState } from "react";
import * as services from "../services/adminPermissionApi";
import { useDispatch, useSelector } from "react-redux";
import { setAdminRoleList, setIsLoading } from "../slices/adminPermissionSlice";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";

function toParams(params = {}) {
    const { pagination = {}, sortBy = [] } = params;
    const modifiedParams = {
        ...params,
        pagination: {
            page: pagination.page + 1,
            size: pagination.pageSize,
        },
        sortBy: sortBy
            .filter((s) => s.order !== undefined)
            .map((s) => ({
                direction: s.order === "descend" ? "desc" : "asc",
                property: s.field,
            })),
    };
    return modifiedParams;
}

const useAdminRole = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [pagination, setPagination] = useState({ page: 1, size: 10 });
    const { adminRoleList } = useSelector((state) => state.adminPermission);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const getAdminRoleList = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getStmRoleAdmin({
                    ...params,
                    page: params.page,
                    size: params.size
                });

                dispatch(setAdminRoleList(response?.data?.page?.content || []));
                setPagination({
                    current: response?.data?.page?.number + 1,
                    total: response?.data?.page?.totalElements,
                    size: response?.data?.page?.size
                });

            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch]
    );

    const onStatus = useCallback(
        async (val, record) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: t("dialog.label.confirm_change_status"),
                okText: t("common.confirm"),
                cancelText: t("common.cancel"),
                onOk: async () => {
                    try {
                        dispatch(setIsLoading(true));
                        await services.updateAdminStatusFlag({
                            stmRoleAdminId: record?.id,
                            statusFlag: val ? "ACTIVE" : "INACTIVE",
                        }
                        );

                        message.success(t("common.successsfull"));
                        await getAdminRoleList({
                            page: pagination.current - 1,
                            size: pagination.size
                        });
                    } catch (error) {
                        // alert({ type: "error", resultObject: error });
                    } finally {
                        dispatch(setIsLoading(false));
                    }
                }
            })
        },
        [t, dispatch, getAdminRoleList, pagination]
    );

    const onDelete = useCallback(
        async (record) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: t("dialog.confirmation.delete"),
                okText: t("common.confirm"),
                cancelText: t("common.cancel"),
                onOk: async () => {
                    try {
                        dispatch(setIsLoading(true));
                        await services.updateAdminStatusFlag({
                            stmRoleAdminId: record?.id,
                            statusFlag: "DELETE",
                        }
                        );

                        message.success(t("common.successsfull"));
                        await getAdminRoleList({
                            page: pagination.current - 1,
                            size: pagination.size
                        });
                    } catch (error) {
                        // alert({ type: "error", resultObject: error });
                    } finally {
                        dispatch(setIsLoading(false));
                    }
                }
            })

        },
        [t, dispatch, getAdminRoleList, pagination]
    );

    const onCreate = useCallback(() => {
        setIsOpenModal(true);
        setSelectedRecord(null);
    }, [setIsOpenModal]);

    const onEdit = useCallback((record) => {
        setIsOpenModal(true);
        setIsEdit(true);
        setSelectedRecord(record);
    }, [])

    const onClose = useCallback(() => {
        setIsOpenModal(false);
        setIsEdit(false);
        setSelectedRecord(null);
    }, [])

    const createAdminRole = useCallback(
        async (values = {}) => {
            try {
                dispatch(setIsLoading(true));
                await services.updateStmRoleAdmin(
                    { ...values }
                );

                message.success(t("common.successsfull"));
                onClose();
                getAdminRoleList({ page: 0, size: 10 });
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch, onClose, getAdminRoleList]
    );

    const updateAdminRole = useCallback(
        async (values = {}) => {
            try {
                dispatch(setIsLoading(true));
                await services.updateStmRoleAdmin(
                    toParams({
                        stmRoleAdminId: values?.id,
                        roleName: values?.roleName,
                        roleNameEn: values?.roleNameEn,
                        description: values?.description,
                        descriptionEn: values?.descriptionEn
                    })
                );

                message.success(t("common.successsfull"));
                onClose();
                getAdminRoleList({ page: 0, size: 10 });
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch, onClose, getAdminRoleList]
    );

    useEffect(() => {
        getAdminRoleList({ page: 0, size: 10 });
    }, []);

    const onSubmit = useCallback(
        (values = {}) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: (
                    <> {i18n.language == "en" ?
                        <p>Are you sure confirm to {isEdit ? "edit" : "create"} ?</p>
                        : <p>คุณแน่ใจหรือไม่ที่จะ {isEdit ? "แก้ไข" : "สร้างใหม่"} ?</p>}
                    </>
                ),
                onOk() {
                    if (isEdit) {
                        updateAdminRole(values);
                    } else {
                        createAdminRole(values);
                    }
                },
                okText: t("common.confirm"),
                cancelText: t("common.cancel"),
            });
        },
        [t, i18n.language, isEdit, updateAdminRole, createAdminRole]
    );

    const handleSearch = (values) => {
        try {
            const filters = {
                ...values,
            };

            getAdminRoleList({
                page: 0,
                size: 10,
                ...filters
            });
        } catch (error) { }
    }

    return {
        pagination,
        adminRoleList,
        isOpenModal,
        isEdit,
        selectedRecord,
        onStatus,
        onEdit,
        onClose,
        onSubmit,
        onCreate,
        onDelete,
        handleSearch
    }
}
export default useAdminRole;